import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAgingProcessCase,
  fetchAgingSettledCase,
  fetchBusinessSnapshot,
  fetchCghsStatusTable,
  fetchCghsStatusTableList,
  fetchDeduction,
  fetchDeductionDetailsData,
  fetchDSOData,
  fetchMonthOnMonthClaimStatus,
  fetchPatientListAgingSettledCase,
  fetchSchemes,
  fetchSchemeTableData,
} from "../apis/dashboardService";
// import { fetchDashboardData } from "../apis/dashboardService";

interface BusinessSnapshotParams {
  hospitalId: number;
  startDate: string;
  endDate: string;
}

interface MonthOnMonthData {
  hospitalId: number;
  startDate: string;
  endDate: string;
  schemeId: number;
  visitType: number;
  type: string;
}

interface MonthOnMonthDataTable {
  hospitalId: number;
  startDate: string;
  endDate: string;
  schemeId: number;
  visitType: number;
}

export const getBusinessSnapshot = createAsyncThunk<
  any,
  BusinessSnapshotParams,
  { rejectValue: any }
>(
  "dashboard/getBusinessSnapshot",
  async ({ hospitalId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const data = await fetchBusinessSnapshot(hospitalId, startDate, endDate);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getDSOData = createAsyncThunk<
  any,
  BusinessSnapshotParams,
  { rejectValue: any }
>(
  "dashboard/getDSOData",
  async ({ hospitalId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const data = await fetchDSOData(hospitalId, startDate, endDate);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getDeductionDetailsData = createAsyncThunk<
  any,
  MonthOnMonthData,
  { rejectValue: any }
>(
  "dashboard/getDeductionDetails",
  async (
    { hospitalId, startDate, endDate, schemeId, visitType, type },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchDeductionDetailsData(
        hospitalId,
        startDate,
        endDate,
        schemeId,
        visitType,
        type
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getDeduction = createAsyncThunk<
  any,
  MonthOnMonthData,
  { rejectValue: any }
>(
  "dashboard/getDeduction",
  async (
    { hospitalId, startDate, endDate, schemeId, visitType, type },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchDeduction(
        hospitalId,
        startDate,
        endDate,
        schemeId,
        visitType,
        type
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);
export const getMonthOnMonthClaimStatus = createAsyncThunk<
  any,
  MonthOnMonthDataTable,
  { rejectValue: any }
>(
  "dashboard/monthOnMonthClaimStatus",
  async (
    { hospitalId, startDate, endDate, schemeId, visitType },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchMonthOnMonthClaimStatus(
        hospitalId,
        startDate,
        endDate,
        schemeId,
        visitType
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getAgingSettledCase = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/AgingSettledCase",
  async ({ hospitalId, schemeId, visitType }, { rejectWithValue }) => {
    try {
      const data = await fetchAgingSettledCase(hospitalId, schemeId, visitType);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getPatientListAgingSettledCase = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/patientListAgingSettledCase",
  async (
    { hospitalId, schemeId, visitType, interval, month, settled },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchPatientListAgingSettledCase(
        hospitalId,
        schemeId,
        visitType,
        interval,
        month,
        settled
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getAgingProcessCase = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/AgingProcessCase",
  async ({ hospitalId, schemeId, visitType }, { rejectWithValue }) => {
    try {
      const data = await fetchAgingProcessCase(hospitalId, schemeId, visitType);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getSchemeTableData = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/SchemeTableData",
  async (
    { hospitalId, startDate, endDate, schemeName },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchSchemeTableData(
        hospitalId,
        startDate,
        endDate,
        schemeName,
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "An error occured");
    }
  }
);

export const downloadSchemeTableData = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/downloadTableData",
  async (
    { hospitalId, startDate, endDate, schemeName },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchSchemeTableData(
        hospitalId,
        startDate,
        endDate,
        schemeName,
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "An error occured");
    }
  }
);

export const getCghsStatusTable = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/CghsStatusTable",
  async (
    { hospitalId, startDate, endDate, schemeId, visitType },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchCghsStatusTable(
        hospitalId,
        startDate,
        endDate,
        schemeId,
        visitType
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getCghsStatusTableList = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/CghsStatusTableList",
  async (
    { hospitalId, startDate, endDate, schemeId, visitType, status },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchCghsStatusTableList(
        hospitalId,
        startDate,
        endDate,
        schemeId,
        visitType,
        status
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getAllSchemesForDashboard = createAsyncThunk<
  any,
  any,
  { rejectValue: any }
>(
  "dashboard/SchemeOfDashboard",
  async ({ hospitalId }, { rejectWithValue }) => {
    try {
      const data = await fetchSchemes(hospitalId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    businessSnapshot: [],
    DSOData: [],
    BSSApproved: "",
    BSSGrossSettled: "",
    BSSinProcess: "",
    deductionDetails: [],
    deduction: [],
    monthOnMonthClaimStatusData: [],
    dashboardCghsStatusTableSubResponseList: [],
    agingProcessData: [],
    schemeTableData: [],
    agingSettledData: [],
    schemesOfDashBoard: [],
    dashboardCghsStatusTableSubResponsePatientList: [],
    loading: false,
    isFetchingCghsStatusTable: false,
    isFetchingSchemes: false,
    isFetchingMonthOnMonthClaimStatus: false,
    isFetchingBSS: false,
    isFetchingDSO: false,
    isFetchingSchemeTableData: false,
    isFetchingCghsStatusTableList: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessSnapshot.pending, (state) => {
        state.loading = true;
        state.isFetchingBSS = true;
        state.error = null;
      })
      .addCase(getBusinessSnapshot.fulfilled, (state, action) => {
        state.loading = false;
        state.isFetchingBSS = false;
        state.businessSnapshot =
          action.payload.dashboardBusinessSnapshotSubResponseList;
        state.BSSApproved = action.payload.approved;
        state.BSSGrossSettled = action.payload.grossSettled;
        state.BSSinProcess = action.payload.inProcess;
      })
      .addCase(getBusinessSnapshot.rejected, (state, action) => {
        state.loading = false;
        state.isFetchingBSS = false;
        state.error = action.payload;
      })
      .addCase(getDSOData.pending, (state) => {
        state.loading = true;
        state.isFetchingDSO = true;
        state.error = null;
      })
      .addCase(getDSOData.fulfilled, (state, action) => {
        state.loading = false;
        state.isFetchingDSO = false;
        state.DSOData = action.payload.daySalesOutstandingSubResponseList;
      })
      .addCase(getDSOData.rejected, (state, action) => {
        state.loading = false;
        state.isFetchingDSO = false;
        state.error = action.payload;
      })
      .addCase(getDeductionDetailsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDeductionDetailsData.fulfilled, (state, action) => {
        state.loading = false;
        state.deductionDetails = action.payload.deductionSubResponseList;
      })
      .addCase(getDeductionDetailsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDeduction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDeduction.fulfilled, (state, action) => {
        state.loading = false;
        state.deduction = action.payload.deductionSubResponseList;
      })
      .addCase(getDeduction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMonthOnMonthClaimStatus.pending, (state) => {
        state.isFetchingMonthOnMonthClaimStatus = true;
        state.error = null;
      })
      .addCase(getMonthOnMonthClaimStatus.fulfilled, (state, action) => {
        state.isFetchingMonthOnMonthClaimStatus = false;
        state.monthOnMonthClaimStatusData =
          action.payload.monthOnMonthClaimStatusSubResponseList;
      })
      .addCase(getMonthOnMonthClaimStatus.rejected, (state, action) => {
        state.isFetchingMonthOnMonthClaimStatus = false;
        state.error = action.payload;
      })
      .addCase(getAgingProcessCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAgingProcessCase.fulfilled, (state, action) => {
        state.loading = false;
        state.agingProcessData = action.payload.agingInProcessSubResponseList;
      })
      .addCase(getAgingProcessCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAgingSettledCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAgingSettledCase.fulfilled, (state, action) => {
        state.loading = false;
        state.agingSettledData =
          action.payload.agingInSettledCaseSubResponseList;
      })
      .addCase(getAgingSettledCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSchemeTableData.pending, (state) => {
        state.isFetchingSchemeTableData = true;
        state.error = null;
      })
      .addCase(getSchemeTableData.fulfilled, (state, action) => {
        state.isFetchingSchemeTableData = false;
        state.schemeTableData = action.payload.cghsDataTableSubResponseList;
      })
      .addCase(getSchemeTableData.rejected, (state, action) => {
        state.isFetchingSchemeTableData = false;
        state.error = action.payload;
      })
      .addCase(getCghsStatusTable.pending, (state) => {
        state.isFetchingCghsStatusTable = true;
        state.error = null;
      })
      .addCase(getCghsStatusTable.fulfilled, (state, action) => {
        state.isFetchingCghsStatusTable = false;
        state.dashboardCghsStatusTableSubResponseList =
          action.payload.dashboardCghsStatusTableSubResponseList;
      })
      .addCase(getCghsStatusTable.rejected, (state, action) => {
        state.isFetchingCghsStatusTable = false;
        state.error = action.payload;
      })

      .addCase(getCghsStatusTableList.pending, (state) => {
        state.isFetchingSchemeTableData = true;
        state.error = null;
      })
      .addCase(getCghsStatusTableList.fulfilled, (state, action) => {
        state.isFetchingSchemeTableData = false;
        state.schemeTableData = action.payload.cghsDataTableSubResponseList;
      })
      .addCase(getCghsStatusTableList.rejected, (state, action) => {
        state.isFetchingSchemeTableData = false;
        state.error = action.payload;
      })

      .addCase(getPatientListAgingSettledCase.pending, (state) => {
        state.isFetchingCghsStatusTable = true;
        state.error = null;
      })
      .addCase(getPatientListAgingSettledCase.fulfilled, (state, action) => {
        state.isFetchingCghsStatusTable = false;
        state.schemeTableData = action.payload.cghsDataTableSubResponseList;
      })
      .addCase(getPatientListAgingSettledCase.rejected, (state, action) => {
        state.isFetchingCghsStatusTable = false;
        state.error = action.payload;
      })
      .addCase(getAllSchemesForDashboard.pending, (state) => {
        state.isFetchingSchemes = true;
        state.error = null;
      })
      .addCase(getAllSchemesForDashboard.fulfilled, (state, action) => {
        state.isFetchingSchemeTableData = false;
        state.schemeTableData = action.payload.monthOnMonthsSchemesSubRespons;
      })
      .addCase(getAllSchemesForDashboard.rejected, (state, action) => {
        state.isFetchingSchemes = false;
        state.error = action.payload;
      });
  },
});

export default dashboardSlice.reducer;
